/**
 * Live-collab Yjs document layer.
 *
 * - One Y.Doc per document id, refcounted so each mount/unmount of an editor
 *   pane can share the same doc between sibling components (EditorPane +
 *   TabBar + sidebar lists) without duplicating state.
 * - `body` (Y.XmlFragment) is the editor content, bound to TipTap via
 *   @tiptap/extension-collaboration.
 * - `meta` (Y.Map) holds title, icon, color, tags — any field that the UI
 *   renders elsewhere and should update live.
 *
 * The server is blind: every Yjs update is encrypted with the vault key
 * client-side, sent over the existing WebSocket as a `yjs_update` message, and
 * relayed verbatim to every other connected session for the same document.
 *
 * Snapshot persistence is debounced (3s idle / 30s max) and uses the existing
 * PUT /api/documents/:id path — the server can't tell the difference between a
 * snapshot upload and the pre-Yjs "save" payload.
 */

import { useEffect, useState } from 'react';
import * as Y from 'yjs';
import {
  Awareness,
  encodeAwarenessUpdate,
  applyAwarenessUpdate,
  removeAwarenessStates,
} from 'y-protocols/awareness';
import {
  MAX_YJS_UPDATE_BYTES,
  MAX_YJS_SYNC_RESPONSE_BYTES,
  type EncryptedPayload,
} from '@dragonhq/protocol';
import { encryptBlob, decryptBlob, decryptJson, encryptJson, toB64, fromB64 } from '../../../core/crypto';
import { useVault } from '../../../core/lib/vault';
import { refreshAccessToken } from '../../../core/lib/api';
import { api } from './api';
import { localStore } from '../db';
import { pullAll, type DocumentPlain } from '../sync';

const SNAPSHOT_IDLE_MS = 3_000;
const SNAPSHOT_MAX_MS = 30_000;

/**
 * Cross-tab Yjs bridge. When the same user has the same document open in
 * two tabs of the same origin, a local edit in tab A used to remain
 * invisible in tab B until the next snapshot PUT landed and emitted a
 * `change_event` (3-30 s window). BroadcastChannel closes that gap to a
 * few milliseconds without involving the server.
 *
 * The channel is same-origin and only the same browser sees it, but we
 * still ship encrypted bytes so the protocol stays uniform with the WS
 * wire format (handleRemoteUpdate is reused unchanged) and so a future
 * worker / extension running in the same origin without the vault key
 * can't peek.
 */
const VAULT_BROADCAST_CHANNEL = 'dragonhq-vault-yjs';
const bc: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(VAULT_BROADCAST_CHANNEL) : null;
type CrossTabMsg =
  | { type: 'yjs'; docId: string; wire: string }
  | { type: 'awareness'; docId: string; wire: string };
if (bc) {
  bc.onmessage = (ev: MessageEvent<CrossTabMsg>) => {
    const msg = ev.data;
    if (!msg || typeof msg !== 'object') return;
    // BroadcastChannel does not deliver to the sender, so any message we
    // see came from another tab. Vault key may be null if the receiving
    // tab is locked — handleRemoteUpdate / handleAwarenessRemoteUpdate
    // both bail in that case.
    const vk = useVault.getState().vaultKey;
    if (!vk) return;
    if (msg.type === 'yjs') {
      void handleRemoteUpdate(msg.docId, msg.wire, vk);
    } else if (msg.type === 'awareness') {
      void handleAwarenessRemoteUpdate(msg.docId, msg.wire, vk);
    }
  };
}
function postCrossTab(msg: CrossTabMsg) {
  if (!bc) return;
  try {
    bc.postMessage(msg);
  } catch {
    /* swallow — same-origin channel, not load-bearing for durability */
  }
}

/**
 * IndexedDB quota observer.
 *
 * The crash-recovery mirror, the `pendingSync` flag, and the boot recovery
 * path all rely on IDB writes succeeding silently. If the user's browser
 * runs out of IDB quota, those writes throw `QuotaExceededError` — the
 * server PUT path still works, but the second-line crash-recovery defense
 * is degraded without anyone knowing. Workspace.tsx subscribes to this
 * signal and surfaces a toast.
 *
 * Re-arms automatically: a successful IDB write after a quota warning
 * resets the latch so a user who clears storage and exhausts it again can
 * see the warning a second time. Other IDB errors (transient transaction
 * abort, etc.) are still swallowed since they're typically benign and
 * re-tried on the next mirror tick.
 */
type LocalCacheFullListener = () => void;
const localCacheFullListeners = new Set<LocalCacheFullListener>();
let localCacheFullEmitted = false;
export function subscribeLocalCacheFull(cb: LocalCacheFullListener): () => void {
  localCacheFullListeners.add(cb);
  return () => localCacheFullListeners.delete(cb);
}
function noteIdbWriteError(err: unknown): void {
  // DOMException is the spec'd shape; fall back to name-string matching for
  // the older Safari quirk (`name === 'QuotaExceededError'` on plain Error).
  const name =
    typeof err === 'object' && err !== null && 'name' in err
      ? String((err as { name: unknown }).name)
      : '';
  if (name !== 'QuotaExceededError' && name !== 'NS_ERROR_DOM_QUOTA_REACHED') return;
  if (localCacheFullEmitted) return;
  localCacheFullEmitted = true;
  for (const cb of localCacheFullListeners) {
    try { cb(); } catch (e) { console.warn('[yjs] localCacheFull listener threw', e); }
  }
}
/** Called after a successful IDB write — re-arms the quota warning latch. */
function noteIdbWriteSuccess(): void {
  if (localCacheFullEmitted) localCacheFullEmitted = false;
}
/**
 * Tight debounce for the local-only IndexedDB mirror. The server-PUT path
 * (SNAPSHOT_IDLE_MS / SNAPSHOT_MAX_MS above) is bandwidth-conscious, so
 * during continuous typing the localStore can be up to 30 s behind the live
 * Y.Doc — and a tab crash in that window loses everything since the last
 * persistSnapshot. This shorter mirror path writes just to IndexedDB (no
 * server round-trip), so on reopen after a crash `acquireDoc` seeds the
 * Y.Doc from a snapshot at most `LOCAL_MIRROR_IDLE_MS` old.
 */
const LOCAL_MIRROR_IDLE_MS = 500;

type SeedFields = Partial<DocumentPlain>;

interface DocState {
  ydoc: Y.Doc;
  meta: Y.Map<unknown>;
  /**
   * Per-doc awareness (presence + cursor). One instance per Y.Doc; lives
   * for the doc's full lifecycle in `docs`. Local state is set by
   * EditorPane (user identity + cursor decorations via TipTap's
   * CollaborationCursor extension); remote peer state arrives via
   * `handleAwarenessRemoteUpdate`. Cleared via `removeAwarenessStates` in
   * `releaseDocAsync` so peers see us leave promptly.
   */
  awareness: Awareness;
  /** Cleanup function returned by `wireAwarenessOutbound`, run on doc destroy. */
  awarenessTeardown?: () => void;
  refCount: number;
  customerId: string;
  parentId: string | null;
  /** Origin marker used on remote-applied updates so our local listener can skip them. */
  remoteOrigin: symbol;
  idleTimer?: ReturnType<typeof setTimeout>;
  maxTimer?: ReturnType<typeof setTimeout>;
  /** Short-cadence IndexedDB-only mirror timer (crash recovery — see LOCAL_MIRROR_IDLE_MS). */
  mirrorTimer?: ReturnType<typeof setTimeout>;
  /** Set to the last encoded Y.Doc state we pushed, for equality short-circuit. */
  lastPushed?: string;
  vaultKey?: CryptoKey;
  /**
   * Last row `version` we observed for this document (set on acquire from the
   * server DTO, bumped on every successful PUT). Sent as `expectedVersion` on
   * persistSnapshot so the server can reject a write that would clobber a
   * concurrent change (409 → retry with merged state).
   */
  lastKnownVersion?: number;
  /**
   * Markdown representation supplied by the currently-mounted editor (via
   * TipTap's HTML → markdown serializer). Kept current so snapshots include
   * the full rich-text representation, not a lossy XmlFragment-derived string.
   */
  derivedMarkdown: string;
  /**
   * Set by `releaseDoc` while the final flush is still in-flight. A
   * concurrent `acquireDoc` that lands during this window reuses the still-
   * live state (refCount bumps back up, the pending flush completes, the
   * caller gets the fully-populated Y.Doc). `pullAll` also consults this +
   * the in-flight PUT set to avoid clobbering a row mid-write.
   */
  releasing?: boolean;
}

const docs = new Map<string, DocState>();
const remoteListeners = new Map<string, Set<(update: Uint8Array) => void>>();

/**
 * Set of docIds with an in-flight `persistSnapshot` PUT. `pullAll` checks
 * this before overwriting a local row — without it, a pullAll that fires
 * between "client sent PUT" and "server wrote it" sees a stale server row
 * and upserts it into localStore with `pendingSync:false`, clobbering the
 * `pendingSync:true` write the snapshot just made. Next `acquireDoc` then
 * seeds the Y.Doc from the clobbered row (empty or stale body).
 */
const snapshotInFlight = new Set<string>();
export function isSnapshotInFlight(docId: string): boolean {
  return snapshotInFlight.has(docId);
}

/**
 * True when a Y.Doc for `docId` is currently held in memory (regardless of
 * release state). Used by `pullAll` to decide whether a freshly-pulled IDB
 * row also needs to be applied to the in-memory Y.Doc — see plan F2.
 */
export function isDocLoaded(docId: string): boolean {
  return docs.has(docId);
}

/**
 * Last-known server version of a loaded Y.Doc, or undefined if not loaded
 * or never set. `pullAll` compares this to the server response to decide
 * whether to call `applyLatestIdbStateToLoadedDoc` — see plan F2.
 */
export function getLoadedDocVersion(docId: string): number | undefined {
  return docs.get(docId)?.lastKnownVersion;
}

/**
 * Apply the freshly-pulled IndexedDB row's yjsState to the in-memory Y.Doc.
 * Caller MUST have just written the latest server row to IDB (this function
 * does NOT itself fetch or write — it just decrypts the local row and
 * merges via the same CRDT-safe path 409-handling uses). No-op if the doc
 * isn't loaded or the vault is locked.
 *
 * Why this exists: on boot/unlock/reconnect, `pullAll` updates IDB but
 * leaves any in-memory Y.Doc untouched. If a Y.Doc is retained across a
 * long idle and the server has advanced (peer edits, sibling-tab snapshots,
 * any direct server modification), the editor would surface the stale
 * in-memory state until a manual Resync. See plan F2.
 */
export async function applyLatestIdbStateToLoadedDoc(docId: string): Promise<void> {
  const state = docs.get(docId);
  if (!state || !state.vaultKey) return;
  const fresh = await localStore.getDocument(docId);
  if (!fresh) return;
  try {
    const plain = await decryptJson<DocumentPlain>(fresh.data.payload, state.vaultKey);
    if (plain.yjsState) {
      // Route through `applyServerSnapshot` (NOT a bare `Y.applyUpdate`) so
      // the `onRemoteApplied` listener chain fires — without that, a
      // read-only viewer's PM view doesn't repaint after the merge and the
      // DOM stays stale until they navigate away. Same invariant as the
      // 409-handling path in mergeFreshServerState.
      applyServerSnapshot(docId, plain.yjsState);
    }
    state.lastKnownVersion = fresh.data.payload.version;
  } catch (err) {
    console.warn('[yjs] applyLatestIdbStateToLoadedDoc failed', err);
  }
}

/**
 * Global NAK listeners — invoked when the server rejects a yjs_update frame
 * (e.g. oversized bulk paste). Ws handler routes messages here; UI layers
 * subscribe to render a toast. We don't scope per-doc because the toast is a
 * global event; the payload carries the documentId if a consumer needs it.
 */
export interface YjsNakEvent {
  documentId: string;
  reason: 'too_large' | 'not_member';
  maxBytes?: number;
}
const nakListeners = new Set<(ev: YjsNakEvent) => void>();
export function onYjsNak(cb: (ev: YjsNakEvent) => void): () => void {
  nakListeners.add(cb);
  return () => nakListeners.delete(cb);
}
export function dispatchYjsNak(ev: YjsNakEvent): void {
  for (const cb of nakListeners) {
    try {
      cb(ev);
    } catch (err) {
      console.warn('[yjs] nak listener threw', err);
    }
  }
}

/** Wire-format wrapper for an encrypted Yjs update travelling over WS. */
interface EncryptedBinary {
  iv: string;
  ct: string;
}

/** Encrypt raw Yjs update bytes. Returns a JSON-serialisable envelope. */
async function encryptBytes(update: Uint8Array, vaultKey: CryptoKey): Promise<EncryptedBinary> {
  const { iv, ciphertext } = await encryptBlob(update, vaultKey);
  return { iv: toB64(iv), ct: toB64(ciphertext) };
}

async function decryptBytes(env: EncryptedBinary, vaultKey: CryptoKey): Promise<Uint8Array> {
  return decryptBlob(fromB64(env.iv), fromB64(env.ct), vaultKey);
}

/**
 * Acquire (or create) the Y.Doc for a document. Call `releaseDoc` exactly once
 * per acquire; the doc is destroyed when the last ref is released.
 */
export function acquireDoc(
  docId: string,
  customerId: string,
  parentId: string | null,
  seed: SeedFields | null,
  vaultKey: CryptoKey,
  /** Current server row version. Used for optimistic-lock on next PUT. */
  initialVersion?: number,
): DocState {
  let state = docs.get(docId);
  if (state) {
    state.refCount++;
    state.vaultKey = vaultKey;
    state.customerId = customerId;
    state.parentId = parentId;
    // A concurrent `releaseDocAsync` may still be awaiting the final flush.
    // Clearing the flag tells it, post-flush, to keep the Y.Doc alive instead
    // of destroying it — so the new consumer gets the fully-populated doc
    // (body, meta, derivedMarkdown) rather than a freshly-seeded empty one.
    state.releasing = false;
    // Only raise our known version (never downgrade) — a stale second acquire
    // shouldn't undo the tracker that persistSnapshot has already bumped.
    if (typeof initialVersion === 'number') {
      if (state.lastKnownVersion === undefined || initialVersion > state.lastKnownVersion) {
        state.lastKnownVersion = initialVersion;
      }
    }
    return state;
  }

  const ydoc = new Y.Doc();
  const meta = ydoc.getMap<unknown>('meta');

  // Seed order: existing yjsState (authoritative — carries its own meta) →
  // plaintext meta fields from the decrypted payload. The plaintext seed
  // covers freshly-created docs whose first `upsertDocument` wrote
  // `{title, icon}` before any Y.Doc existed.
  if (seed?.yjsState) {
    try {
      Y.applyUpdate(ydoc, fromB64(seed.yjsState));
    } catch {
      /* corrupted — fall through to plaintext seed */
    }
  }

  if (meta.size === 0 && seed) {
    ydoc.transact(() => {
      if (seed.title !== undefined) meta.set('title', seed.title);
      if (seed.icon !== undefined) meta.set('icon', seed.icon);
      if (seed.color !== undefined) meta.set('color', seed.color);
      if (seed.tags !== undefined) meta.set('tags', seed.tags);
    });
  }

  state = {
    ydoc,
    meta,
    awareness: new Awareness(ydoc),
    refCount: 1,
    customerId,
    parentId,
    remoteOrigin: Symbol('remote'),
    vaultKey,
    derivedMarkdown: seed?.markdown ?? '',
    // Seed lastPushed to the incoming state — a subsequent snapshot that
    // encodes an unchanged Y.Doc will short-circuit and not upload. Prevents
    // false snapshots on plain tab-switches / remounts of an unedited doc.
    lastPushed: seed?.yjsState,
    lastKnownVersion: initialVersion,
  };
  docs.set(docId, state);
  return state;
}

/**
 * Editor components call this when they produce a fresh markdown rendering of
 * the body (via TipTap's serializer). The next snapshot write will use it.
 */
export function setDerivedMarkdown(docId: string, markdown: string): void {
  const state = docs.get(docId);
  if (!state) return;
  state.derivedMarkdown = markdown;
}

/**
 * If a Y.Doc is currently in memory for this document, return its live
 * derived markdown — this is the freshest representation, updated on every
 * editor transaction. Returns null when no Y.Doc exists, in which case
 * callers should fall back to the IndexedDB/decrypted markdown.
 *
 * Needed because `persistSnapshot` only mirrors to IndexedDB on a 3s idle
 * debounce; anything read from IndexedDB during that window is stale.
 */
export function getLiveDerivedMarkdown(docId: string): string | null {
  const state = docs.get(docId);
  return state ? state.derivedMarkdown : null;
}

/**
 * Return the current body representation (encoded yjsState + derived
 * markdown) of a loaded Y.Doc. Returns null when no Y.Doc is in memory.
 *
 * This is the body-preservation hook used by `sync/index.ts::upsertDocument`:
 * meta-only updates (rename, icon auto-suggest, attachment drop) must carry
 * the live body forward on the PUT, or the server's `documents.payload` is
 * replaced with a body-less payload AND `document_versions` gets a fresh
 * empty-body row. See the "critical invariant" in apps/vault/CLAUDE.md.
 */
export function getLiveBodySnapshot(
  docId: string,
): { yjsState: string; markdown: string } | null {
  const state = docs.get(docId);
  if (!state) return null;
  return {
    yjsState: toB64(Y.encodeStateAsUpdate(state.ydoc)),
    markdown: state.derivedMarkdown,
  };
}

/**
 * Walk the live Y.Doc XmlFragment (if loaded) and collect every
 * `attachmentId` attribute on `inlineImage` / `attachmentChip` ProseMirror
 * nodes. Used as an authoritative source for the attachment-references
 * scanner, since the markdown round-trip has historically stripped empty
 * atom tags before the fix (ZWSP marker) was in place.
 *
 * Returns null when no Y.Doc is loaded.
 */
export function getLiveAttachmentIds(docId: string): Set<string> | null {
  const state = docs.get(docId);
  if (!state) return null;
  const ids = new Set<string>();
  const frag = state.ydoc.getXmlFragment('default');
  walkFragment(frag, ids);
  return ids;
}

function walkFragment(node: any, ids: Set<string>): void {
  if (!node) return;
  // Y.XmlElement has nodeName + getAttribute
  const name: string | undefined = node.nodeName;
  if (name === 'inlineImage' || name === 'attachmentChip') {
    const id = node.getAttribute ? node.getAttribute('attachmentId') : null;
    if (typeof id === 'string' && id.length > 0) ids.add(id);
  }
  // Recurse into children via toArray (works for YXmlFragment + YXmlElement)
  const children = typeof node.toArray === 'function' ? node.toArray() : [];
  for (const child of children) walkFragment(child, ids);
}

/**
 * Update the Y.Doc meta map for a document if it is currently loaded.
 *
 * The sidebar rename paths (DocumentPane / TabBar) write directly to localStore
 * + server via `upsertDocument`. If the doc is also open in the editor, its
 * Y.Doc holds the authoritative meta — `useLiveMeta` observes it for display,
 * and `persistSnapshot` encodes it back on auto-save. Without this call, the
 * rename would be visually stale AND later stomped by persistSnapshot.
 *
 * No-op when no Y.Doc for the document is in memory.
 */
export function updateLiveMeta(
  docId: string,
  fields: Partial<Pick<DocumentPlain, 'title' | 'icon' | 'color' | 'tags'>>,
): void {
  const state = docs.get(docId);
  if (!state) return;
  state.ydoc.transact(() => {
    if (fields.title !== undefined) state.meta.set('title', fields.title);
    if (fields.icon !== undefined) state.meta.set('icon', fields.icon);
    if (fields.color !== undefined) state.meta.set('color', fields.color);
    if (fields.tags !== undefined) state.meta.set('tags', fields.tags);
  });
}

export function releaseDoc(docId: string): void {
  void releaseDocAsync(docId);
}

/**
 * Async variant — callers that can await the flush (e.g. pre-navigation
 * hooks, the Done button, customer-switch handlers) should use this so the
 * snapshot is guaranteed to land before the next user action. The sync
 * `releaseDoc` above delegates here and returns immediately (for React
 * effect-cleanup callers, which cannot be async).
 *
 * The Y.Doc is kept alive across the flush — we mark the state `releasing`
 * so a concurrent `acquireDoc` for the same id can reuse it without
 * observing a half-torn-down doc. The destruction only happens after the
 * `persistSnapshot` promise settles.
 */
export async function releaseDocAsync(docId: string): Promise<void> {
  const state = docs.get(docId);
  if (!state) return;
  state.refCount--;
  if (state.refCount > 0) return;

  // Cancel any pending debounced snapshot — we're about to flush explicitly.
  if (state.idleTimer) {
    clearTimeout(state.idleTimer);
    state.idleTimer = undefined;
  }
  if (state.maxTimer) {
    clearTimeout(state.maxTimer);
    state.maxTimer = undefined;
  }
  if (state.mirrorTimer) {
    clearTimeout(state.mirrorTimer);
    state.mirrorTimer = undefined;
  }

  state.releasing = true;
  try {
    await persistSnapshot(docId);
  } catch {
    /* persistSnapshot already logs on failure; we can't retry here because
       the calling context (unmount, navigation, pagehide) may be tearing
       down. Layer 6 (IndexedDB write-ahead) is the second line of defence
       when the final flush fails. */
  }

  // A concurrent acquireDoc may have bumped refCount during the flush.
  // If so, keep the Y.Doc alive for the new consumer.
  const current = docs.get(docId);
  if (!current) return;
  if (current.refCount > 0) {
    current.releasing = false;
    return;
  }
  // Broadcast our departure so peers see us leave promptly. Done before
  // teardown so the outbound listener has a chance to encode + send.
  try {
    removeAwarenessStates(current.awareness, [current.awareness.clientID], 'local');
  } catch {
    /* non-fatal */
  }
  current.awarenessTeardown?.();
  current.awareness.destroy();
  current.ydoc.destroy();
  docs.delete(docId);
  remoteListeners.delete(docId);
}

export function getDocState(docId: string): DocState | undefined {
  return docs.get(docId);
}

export interface LiveMeta {
  title?: string;
  icon?: string;
  color?: string;
  tags?: string[];
}

/**
 * React hook: returns the live meta fields for a document when its Y.Doc is
 * currently in memory, or null when it isn't. Callers fall back to the
 * decrypted payload for docs without a live Y.Doc.
 */
export function useLiveMeta(docId: string | null | undefined): LiveMeta | null {
  const [meta, setMeta] = useState<LiveMeta | null>(null);

  useEffect(() => {
    if (!docId) {
      setMeta(null);
      return;
    }
    const state = docs.get(docId);
    if (!state) {
      setMeta(null);
      return;
    }
    const snapshot = (): LiveMeta => ({
      title: state.meta.get('title') as string | undefined,
      icon: state.meta.get('icon') as string | undefined,
      color: state.meta.get('color') as string | undefined,
      tags: state.meta.get('tags') as string[] | undefined,
    });
    setMeta(snapshot());
    const observer = () => setMeta(snapshot());
    state.meta.observe(observer);
    return () => state.meta.unobserve(observer);
  }, [docId]);

  return meta;
}

/**
 * Subscribe to remote Yjs updates (after decryption + apply). The callback is
 * invoked AFTER the update has been applied to the local Y.Doc — callers can
 * observe Y.Map changes via Yjs's own observe APIs; this hook is mainly useful
 * for "show a toast that another user just edited".
 */
export function onRemoteApplied(docId: string, cb: (update: Uint8Array) => void): () => void {
  let set = remoteListeners.get(docId);
  if (!set) {
    set = new Set();
    remoteListeners.set(docId, set);
  }
  set.add(cb);
  return () => set!.delete(cb);
}

/**
 * Apply Yjs update bytes to the per-doc Y.Doc AND fire the `onRemoteApplied`
 * listeners — so subscribers (e.g. EditorPane's PM view-flush) run regardless
 * of whether the bytes came from a peer's WS frame or from a server-snapshot
 * re-merge. Without this, applying via `Y.applyUpdate` alone updates the Y.Doc
 * but TipTap (especially in read-only mode, which is the common case for a
 * viewer of someone else's edits) doesn't repaint the DOM until something
 * else triggers a transaction.
 */
function applyRemoteBytes(state: DocState, docId: string, bytes: Uint8Array): void {
  Y.applyUpdate(state.ydoc, bytes, state.remoteOrigin);
  const listeners = remoteListeners.get(docId);
  if (listeners) for (const l of listeners) l(bytes);
}

/**
 * Apply a server snapshot (full or partial yjsState) into the live Y.Doc as
 * if it were a remote update — drives the `onRemoteApplied` listener chain
 * so EditorPane's view-flush fires and read-only viewers see the new state
 * without having to navigate away and back. Used by the `change_event`
 * re-merge and the reconnect-merge in `EditorPane.tsx`.
 */
export function applyServerSnapshot(docId: string, yjsStateB64: string): void {
  const state = docs.get(docId);
  if (!state) return;
  try {
    applyRemoteBytes(state, docId, fromB64(yjsStateB64));
  } catch (err) {
    console.warn('[yjs] applyServerSnapshot failed', err);
  }
}

/**
 * Called by sync/ws.ts when a remote `yjs_update` message lands. `updateJson`
 * is the raw string from the WS frame — a JSON-stringified EncryptedBinary.
 */
export async function handleRemoteUpdate(
  docId: string,
  updateJson: string,
  vaultKey: CryptoKey,
): Promise<void> {
  const state = docs.get(docId);
  if (!state) return;
  let env: EncryptedBinary;
  try {
    env = JSON.parse(updateJson);
  } catch {
    return;
  }
  try {
    const bytes = await decryptBytes(env, vaultKey);
    applyRemoteBytes(state, docId, bytes);
  } catch (err) {
    console.warn('[yjs] failed to apply remote update', err);
  }
}

/**
 * Late-joiner handshake — emit a `yjs_sync_request` carrying our current
 * state vector. State vectors are `Map<clientID, clock>`, plaintext
 * metadata only (no document content), so they travel unencrypted. Peers
 * with the doc loaded reply with the encrypted diff (`yjs_sync_response`)
 * which `handleYjsSyncResponse` applies via the same `state.remoteOrigin`
 * path as a normal update.
 *
 * Fired on `acquireDoc` (initial open) and on WS reconnect (catches up any
 * doc still mounted after a transient disconnect).
 *
 * Forensics: if no peer answers within `SYNC_REQUEST_TIMEOUT_MS`, log it.
 * The next `change_event → pullAll → applyServerSnapshot` cycle still
 * catches us up via the persisted snapshot — but the gap is the kind of
 * thing operators want visible in the journal when investigating "did the
 * tech ever see X's edit?".
 */
const SYNC_REQUEST_TIMEOUT_MS = 10_000;
const pendingSyncRequests = new Map<string, ReturnType<typeof setTimeout>>();
export function requestYjsSync(
  docId: string,
  sendWs: (msg: { type: 'yjs_sync_request'; documentId: string; stateVector: string }) => boolean,
): void {
  const state = docs.get(docId);
  if (!state) return;
  const stateVector = toB64(Y.encodeStateVector(state.ydoc));
  const ok = sendWs({ type: 'yjs_sync_request', documentId: docId, stateVector });
  if (!ok) return; // socket closed; the WS reconnect handler will retry
  const existing = pendingSyncRequests.get(docId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    pendingSyncRequests.delete(docId);
    console.info(
      '[yjs] sync_request unanswered — no peer with the doc loaded; relying on next snapshot pullAll',
      docId,
    );
  }, SYNC_REQUEST_TIMEOUT_MS);
  pendingSyncRequests.set(docId, timer);
}

function clearPendingSyncRequest(docId: string): void {
  const t = pendingSyncRequests.get(docId);
  if (!t) return;
  clearTimeout(t);
  pendingSyncRequests.delete(docId);
}

/**
 * Answer a peer's `yjs_sync_request`. If we have the doc loaded, compute
 * the missing-ops diff (`Y.encodeStateAsUpdate(ydoc, theirSV)`), encrypt
 * with the vault key, and route back via `yjs_sync_response`. Drops
 * silently when the doc isn't loaded, when the diff is empty, or when the
 * encrypted envelope exceeds the response cap.
 */
export async function handleYjsSyncRequest(
  docId: string,
  stateVectorB64: string,
  requesterUserId: string,
  vaultKey: CryptoKey,
  sendWs: (msg: {
    type: 'yjs_sync_response';
    documentId: string;
    toUserId: string;
    update: string;
  }) => boolean,
): Promise<void> {
  const state = docs.get(docId);
  if (!state) return;
  try {
    const theirSV = fromB64(stateVectorB64);
    const diff = Y.encodeStateAsUpdate(state.ydoc, theirSV);
    if (diff.length === 0) return;
    const env = await encryptBytes(diff, vaultKey);
    const wire = JSON.stringify(env);
    if (wire.length > MAX_YJS_SYNC_RESPONSE_BYTES) return;
    sendWs({
      type: 'yjs_sync_response',
      documentId: docId,
      toUserId: requesterUserId,
      update: wire,
    });
  } catch (err) {
    console.warn('[yjs] failed to answer sync_request', err);
  }
}

/**
 * Apply a peer's `yjs_sync_response`. Same decrypt-and-apply path as a
 * normal update — the `state.remoteOrigin` marker prevents `wireOutbound`'s
 * listener from echoing the merged state back as a local broadcast.
 */
export async function handleYjsSyncResponse(
  docId: string,
  updateJson: string,
  vaultKey: CryptoKey,
): Promise<void> {
  clearPendingSyncRequest(docId);
  return handleRemoteUpdate(docId, updateJson, vaultKey);
}

/**
 * Get the per-doc Yjs Awareness instance. Used by EditorPane to wire the
 * TipTap CollaborationCursor extension and by PresenceBar to enumerate
 * peers currently viewing the doc.
 */
export function getAwareness(docId: string): Awareness | undefined {
  return docs.get(docId)?.awareness;
}

/** Origin marker on `applyAwarenessUpdate` so the local update listener skips remote echoes. */
const AWARENESS_REMOTE_ORIGIN = 'remote';

/**
 * Wire the per-doc Awareness to outbound WS broadcast: every local change
 * (cursor move, selection, user identity, departure) is encrypted with the
 * vault key and sent as `awareness_update`. Returns a teardown function;
 * stored on `state.awarenessTeardown` so `releaseDocAsync` can call it
 * before destroying the awareness instance.
 */
export function wireAwarenessOutbound(
  docId: string,
  vaultKey: CryptoKey,
  sendWs: (msg: { type: 'awareness_update'; documentId: string; update: string }) => boolean,
): () => void {
  const state = docs.get(docId);
  if (!state) return () => undefined;
  // If already wired, tear down first (acquireDoc reused an existing state).
  state.awarenessTeardown?.();

  const handler = async (
    { added, updated, removed }: { added: number[]; updated: number[]; removed: number[] },
    origin: unknown,
  ) => {
    if (origin === AWARENESS_REMOTE_ORIGIN) return; // don't echo remote updates back
    const changed = [...added, ...updated, ...removed];
    if (changed.length === 0) return;
    try {
      const update = encodeAwarenessUpdate(state.awareness, changed);
      const env = await encryptBytes(update, vaultKey);
      const wire = JSON.stringify(env);
      if (wire.length > MAX_YJS_UPDATE_BYTES) return;
      sendWs({ type: 'awareness_update', documentId: docId, update: wire });
      postCrossTab({ type: 'awareness', docId, wire });
    } catch (err) {
      console.warn('[awareness] failed to broadcast', err);
    }
  };
  state.awareness.on('update', handler);

  const teardown = () => {
    state.awareness.off('update', handler);
    state.awarenessTeardown = undefined;
  };
  state.awarenessTeardown = teardown;
  return teardown;
}

/**
 * Apply an inbound `awareness_update` from a peer. Decrypts the envelope
 * and feeds it into the per-doc Awareness instance via the standard
 * y-protocols `applyAwarenessUpdate`. Tagged with the remote origin so
 * the outbound listener does not echo it back.
 */
export async function handleAwarenessRemoteUpdate(
  docId: string,
  updateJson: string,
  vaultKey: CryptoKey,
): Promise<void> {
  const state = docs.get(docId);
  if (!state) return;
  let env: EncryptedBinary;
  try {
    env = JSON.parse(updateJson);
  } catch {
    return;
  }
  try {
    const bytes = await decryptBytes(env, vaultKey);
    applyAwarenessUpdate(state.awareness, bytes, AWARENESS_REMOTE_ORIGIN);
  } catch (err) {
    console.warn('[awareness] failed to apply remote update', err);
  }
}

/**
 * Wire a Y.Doc into outbound broadcast: every local (non-remote) transaction
 * is encrypted and sent over the WS. Returns a teardown function.
 */
export function wireOutbound(
  docId: string,
  vaultKey: CryptoKey,
  sendWs: (msg: { type: 'yjs_update'; documentId: string; update: string }) => void,
  scheduleSnapshot: () => void,
): () => void {
  const state = docs.get(docId);
  if (!state) return () => undefined;

  const listener = async (update: Uint8Array, origin: unknown) => {
    if (origin === state.remoteOrigin) return; // don't echo remote updates back
    // Read the live key off the doc state, not the captured `vaultKey`
    // parameter — defence in depth against a future refactor that ever
    // reuses a Y.Doc across lock/unlock cycles. Currently safe because
    // `acquireDoc` always builds a fresh Y.Doc and `releaseDoc` destroys
    // it, so the listener parameter and `state.vaultKey` always agree.
    const liveKey = docs.get(docId)?.vaultKey ?? vaultKey;
    if (!liveKey) return;
    scheduleSnapshot();
    try {
      const env = await encryptBytes(update, liveKey);
      const wire = JSON.stringify(env);
      if (wire.length > MAX_YJS_UPDATE_BYTES) {
        // Pre-send check: a bulk paste would otherwise be dropped by the
        // server with a NAK. We catch it here so offline/disconnected
        // clients also see the toast (they'd never receive the NAK).
        // The update is still in the local Y.Doc, so it's in the next
        // snapshot PUT — only the live-collab broadcast is lost.
        dispatchYjsNak({
          documentId: docId,
          reason: 'too_large',
          maxBytes: MAX_YJS_UPDATE_BYTES,
        });
        return;
      }
      sendWs({ type: 'yjs_update', documentId: docId, update: wire });
      // Mirror to other tabs of the same user via BroadcastChannel so a
      // second tab sees the edit in milliseconds, not 3-30 s after the
      // next snapshot PUT triggers a change_event + pullAll cycle.
      postCrossTab({ type: 'yjs', docId, wire });
    } catch (err) {
      console.warn('[yjs] failed to broadcast local update', err);
    }
  };
  state.ydoc.on('update', listener);
  return () => state.ydoc.off('update', listener);
}

/**
 * Force an immediate snapshot for `docId`, cancelling any pending debounce.
 * Use this at navigation boundaries (customer-switch, page-switch, Done
 * button, pagehide) so the last burst of edits can't be lost to an editor
 * unmount that happens before the 3 s idle / 30 s max timer fires.
 *
 * Returns the underlying `persistSnapshot` promise so callers can await.
 */
export async function flushDoc(docId: string): Promise<void> {
  const state = docs.get(docId);
  if (!state) return;
  if (state.idleTimer) {
    clearTimeout(state.idleTimer);
    state.idleTimer = undefined;
  }
  if (state.maxTimer) {
    clearTimeout(state.maxTimer);
    state.maxTimer = undefined;
  }
  if (state.mirrorTimer) {
    clearTimeout(state.mirrorTimer);
    state.mirrorTimer = undefined;
  }
  await persistSnapshot(docId);
}

/**
 * Flush every doc currently in memory. Used by the pagehide handler to
 * belt-and-braces the tab-close case.
 */
export async function flushAllLiveDocs(): Promise<void> {
  const ids = Array.from(docs.keys());
  await Promise.allSettled(ids.map((id) => flushDoc(id)));
}

/**
 * Lock-time wipe: flush every loaded Y.Doc, then destroy them all. Called
 * from the lock callback so a Y.Doc never survives a lock with its
 * pre-lock body intact (which would surface as stale content on unlock —
 * see plan F3). Safe to call even with no docs loaded.
 *
 * Ordering matters:
 *   1. Cancel pending mirror / persist timers so no encrypt-needing write
 *      fires while we're tearing down.
 *   2. Await `flushAllLiveDocs()` — uses each state's captured CryptoKey,
 *      which `vault.lock()` does NOT mutate (only `useVault.vaultKey` is
 *      nulled), so the flush still succeeds.
 *   3. Destroy each Y.Doc + Awareness, clear the `docs` map.
 */
export async function releaseAllLiveDocs(): Promise<void> {
  for (const state of docs.values()) {
    if (state.idleTimer) {
      clearTimeout(state.idleTimer);
      state.idleTimer = undefined;
    }
    if (state.maxTimer) {
      clearTimeout(state.maxTimer);
      state.maxTimer = undefined;
    }
    if (state.mirrorTimer) {
      clearTimeout(state.mirrorTimer);
      state.mirrorTimer = undefined;
    }
  }
  await flushAllLiveDocs();
  for (const [docId, state] of Array.from(docs.entries())) {
    try {
      removeAwarenessStates(state.awareness, [state.awareness.clientID], 'local');
    } catch {
      /* non-fatal */
    }
    state.awarenessTeardown?.();
    state.awareness.destroy();
    state.ydoc.destroy();
    docs.delete(docId);
    remoteListeners.delete(docId);
  }
}

/**
 * Pagehide-mode flush: dispatch one fire-and-forget keepalive PUT per
 * loaded doc. Distinct from `flushAllLiveDocs` because:
 *
 *  - `keepalive: true` instructs the browser to let the request finish even
 *    after the page goes away. Plain async fetch can be cancelled when the
 *    tab dies; keepalive's 64 KB body cap is the tradeoff.
 *  - No retry loop, no IDB mirror chatter, no waiting for response — the
 *    point is to get the bytes onto the wire before the tab is gone. The
 *    IDB mirror pass in the pagehide handler is the fallback for anything
 *    too large for keepalive or otherwise lost.
 *  - We re-encode the live Y.Doc one last time so the snapshot reflects
 *    every keystroke up to the unload event.
 *
 * Errors are swallowed: the IDB mirror + boot recovery + outbox cover the
 * loss case, and the pagehide handler can't await anything anyway.
 */
export function flushAllLiveDocsForUnload(): void {
  for (const docId of docs.keys()) {
    const state = docs.get(docId);
    if (!state || !state.vaultKey) continue;
    // Skip docs already mid-flight in the regular retry loop. That PUT
    // already has the latest snapshot bytes and a per-doc mutex; firing a
    // duplicate keepalive would just produce a 409 on the server (and a
    // double version increment in the worst case).
    if (isSnapshotInFlight(docId)) continue;
    const vaultKey = state.vaultKey;
    void (async () => {
      try {
        const yjsState = toB64(Y.encodeStateAsUpdate(state.ydoc));
        if (state.lastPushed === yjsState) return;
        const meta = state.meta;
        const plain: DocumentPlain = {
          title: (meta.get('title') as string | undefined) ?? '',
          markdown: state.derivedMarkdown,
          icon: meta.get('icon') as string | undefined,
          color: meta.get('color') as string | undefined,
          tags: meta.get('tags') as string[] | undefined,
          yjsState,
        };
        const payload: EncryptedPayload = await encryptJson(plain, vaultKey);
        const expectedVersion =
          typeof state.lastKnownVersion === 'number' && state.lastKnownVersion >= 1
            ? state.lastKnownVersion
            : undefined;
        await api.updateDocument(
          docId,
          {
            customerId: state.customerId,
            parentId: state.parentId,
            payload,
            expectedVersion,
          },
          { keepalive: true },
        );
      } catch {
        /* swallow — IDB mirror + boot recovery cover this case */
      }
    })();
  }
}

/**
 * Enumerate docIds currently holding a live Y.Doc in memory. Used by the
 * pagehide handler to write each to the IndexedDB mirror synchronously
 * before the tab dies.
 */
export function getLoadedDocIds(): string[] {
  return Array.from(docs.keys());
}

/**
 * Debounced snapshot scheduler: call this whenever a local Y.Doc transaction
 * happens. Fires `persistSnapshot` on idle-3s, or at latest 30s after the
 * first pending edit in the current burst. Also triggers the tight-debounce
 * IndexedDB mirror so a tab crash between server PUTs doesn't lose more
 * than ~LOCAL_MIRROR_IDLE_MS of edits.
 */
export function schedulePersist(docId: string): void {
  const state = docs.get(docId);
  if (!state) return;
  scheduleLocalMirror(docId);
  if (state.idleTimer) clearTimeout(state.idleTimer);
  state.idleTimer = setTimeout(() => {
    state.idleTimer = undefined;
    if (state.maxTimer) {
      clearTimeout(state.maxTimer);
      state.maxTimer = undefined;
    }
    void persistSnapshot(docId).catch(() => undefined);
  }, SNAPSHOT_IDLE_MS);
  if (!state.maxTimer) {
    state.maxTimer = setTimeout(() => {
      state.maxTimer = undefined;
      if (state.idleTimer) {
        clearTimeout(state.idleTimer);
        state.idleTimer = undefined;
      }
      void persistSnapshot(docId).catch(() => undefined);
    }, SNAPSHOT_MAX_MS);
  }
}

/**
 * Schedule a short-debounce local-only IndexedDB mirror. Writes the
 * current Y.Doc state + derived markdown + meta into
 * `localStore.documents` (with `pendingSync:true`) so `acquireDoc` after a
 * reopen — including one caused by a tab crash mid-edit — seeds from a
 * snapshot at most `LOCAL_MIRROR_IDLE_MS` old. No server round-trip: the
 * server-PUT path's debounce (3 s idle / 30 s max) still governs outbound
 * bandwidth.
 *
 * Safe to call on every Y.Doc update; the debounce coalesces.
 */
function scheduleLocalMirror(docId: string): void {
  const state = docs.get(docId);
  if (!state) return;
  if (state.mirrorTimer) clearTimeout(state.mirrorTimer);
  state.mirrorTimer = setTimeout(() => {
    state.mirrorTimer = undefined;
    void writeLocalMirror(docId).catch(() => undefined);
  }, LOCAL_MIRROR_IDLE_MS);
}

/**
 * Encrypt the current Y.Doc state and write it into `localStore.documents`.
 * Does NOT call the server. Used by the crash-recovery mirror path and
 * (optionally) by the pagehide handler when a full persistSnapshot can't
 * complete in time.
 */
export async function writeLocalMirror(docId: string): Promise<void> {
  const state = docs.get(docId);
  if (!state || !state.vaultKey) return;

  const yjsState = toB64(Y.encodeStateAsUpdate(state.ydoc));
  const meta = state.meta;
  const plain: DocumentPlain = {
    title: (meta.get('title') as string | undefined) ?? '',
    markdown: state.derivedMarkdown,
    icon: meta.get('icon') as string | undefined,
    color: meta.get('color') as string | undefined,
    tags: meta.get('tags') as string[] | undefined,
    yjsState,
  };
  const payload: EncryptedPayload = await encryptJson(plain, state.vaultKey);
  try {
    const row = await localStore.getDocument(docId);
    const now = new Date().toISOString();
    await localStore.putDocument({
      id: docId,
      createdAt: row?.createdAt ?? now,
      updatedAt: now,
      deletedAt: row?.deletedAt ?? null,
      pendingSync: true,
      data: {
        id: docId,
        customerId: state.customerId,
        parentId: state.parentId,
        payload,
        deletedAt: row?.deletedAt ?? null,
        createdAt: row?.createdAt ?? now,
        updatedAt: now,
      },
    });
    noteIdbWriteSuccess();
  } catch (err) {
    noteIdbWriteError(err);
  }
}

/**
 * Per-document mutex. A second `persistSnapshot` call for the same docId
 * waits on the first instead of racing it (two parallel PUTs of slightly
 * different snapshots would each 409 the other and waste retry attempts).
 */
const docMutex = new Map<string, Promise<void>>();

/**
 * Retry status emitter. Drives the visible "Saving N edit(s)…" indicator
 * via `useSyncStatus`. Per-doc map; subscribers re-read on each event.
 */
export interface RetryStatus {
  /** Number of failed attempts since the last successful PUT. */
  attempts: number;
  /** True while a retry is being scheduled (the user should see "Saving…"). */
  retrying: boolean;
  /** Last error string, if the previous attempt failed. */
  lastError?: string;
}
const retryStatus = new Map<string, RetryStatus>();
const retryStatusListeners = new Set<() => void>();

export function getRetryStatus(docId: string): RetryStatus | undefined {
  return retryStatus.get(docId);
}

export function listRetryingDocs(): string[] {
  const out: string[] = [];
  for (const [id, s] of retryStatus) {
    if (s.retrying || s.attempts > 0) out.push(id);
  }
  return out;
}

export function subscribeRetryStatus(cb: () => void): () => void {
  retryStatusListeners.add(cb);
  return () => retryStatusListeners.delete(cb);
}

function setRetryStatus(docId: string, s: RetryStatus | null): void {
  if (s === null) retryStatus.delete(docId);
  else retryStatus.set(docId, s);
  for (const l of retryStatusListeners) {
    try { l(); } catch (err) { console.warn('[yjs] retry-status listener threw', err); }
  }
}

/**
 * Permanent-error emitter. A snapshot PUT that returns 404/401/403/410 is
 * unrecoverable from the retry loop's perspective — the doc is gone, the
 * user lost auth, or their role was revoked. Surface it so the UI can
 * present a clear modal (e.g. "save your edits as a new page" for 404).
 */
export interface PermanentSnapshotError {
  documentId: string;
  status: number;
  message: string;
}
const permanentErrorListeners = new Set<(e: PermanentSnapshotError) => void>();

export function onPermanentSnapshotError(
  cb: (e: PermanentSnapshotError) => void,
): () => void {
  permanentErrorListeners.add(cb);
  return () => permanentErrorListeners.delete(cb);
}

function emitPermanentError(e: PermanentSnapshotError): void {
  // Persist a breadcrumb so the status light stays red (and the user can
  // open Recover-failed-edits) until acknowledged. Fire-and-forget — the
  // toast is the synchronous signal; this is the durable one. See plan F4.
  void localStore
    .appendFailedEvent({
      eventId: `snapshot-${e.documentId}-${Date.now()}`,
      source: 'snapshot',
      documentId: e.documentId,
      status: e.status,
      message: e.message,
      at: new Date().toISOString(),
    })
    .catch((err) => console.warn('[yjs] appendFailedEvent failed', err));
  for (const l of permanentErrorListeners) {
    try { l(e); } catch (err) { console.warn('[yjs] permanent-error listener threw', err); }
  }
}

/**
 * Fired once when a save loses an optimistic-lock 409 and we CRDT-merge the
 * peer's state into the local Y.Doc. No data is lost (Yjs combines both
 * sides), but the user benefits from a one-line "your edits were merged
 * with another editor's changes" toast so an unexpected text shift doesn't
 * read as a glitch. Workspace.tsx subscribes and toasts.
 */
export interface ConflictMergedEvent {
  documentId: string;
}
const conflictMergedListeners = new Set<(e: ConflictMergedEvent) => void>();

export function onConflictMerged(cb: (e: ConflictMergedEvent) => void): () => void {
  conflictMergedListeners.add(cb);
  return () => conflictMergedListeners.delete(cb);
}

function emitConflictMerged(e: ConflictMergedEvent): void {
  for (const l of conflictMergedListeners) {
    try { l(e); } catch (err) { console.warn('[yjs] conflict-merged listener threw', err); }
  }
}

function isPermanentError(err: any): boolean {
  const s = err?.status;
  // 400 schema rejection / 404 gone / 403 authz / 410 deleted — retrying never recovers.
  if (s === 400 || s === 404 || s === 403 || s === 410) return true;
  // 401 is split: session_revoked is definitive (admin cutoff or explicit
  // logout — re-auth needed before any retry can succeed) but a bare
  // `unauthorized` is most often a freshly-expired token or a transient
  // captive-portal/MITM blip on cafe wifi. Treating the latter as permanent
  // silently drops edits the user believes are saving — see plan F3.
  if (s === 401 && err?.body?.error === 'session_revoked') return true;
  return false;
}

const RETRY_BASE_MS = 500;
const RETRY_CAP_MS = 30_000;

function backoffMs(attempt: number): number {
  const exp = Math.min(attempt, 6);
  return Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** exp);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Encode the current Y.Doc state, encrypt with the vault key, and PUT to
 * the server. Retries indefinitely with exponential backoff on transient
 * errors (network, 5xx) and merges-then-retries on optimistic-lock 409s
 * (CRDT merge guarantees no edits are lost on either side). Stops only
 * on success, on a permanent error (404/401/403/410, surfaced to the UI),
 * or when the doc is unloaded and the vault is gone (boot recovery picks
 * it up next session via the pendingSync IndexedDB row).
 *
 * Per-doc mutex prevents two concurrent calls from racing each other.
 */
export async function persistSnapshot(docId: string): Promise<void> {
  const inflight = docMutex.get(docId);
  if (inflight) return inflight;
  const promise = persistSnapshotInner(docId).finally(() => {
    if (docMutex.get(docId) === promise) docMutex.delete(docId);
  });
  docMutex.set(docId, promise);
  return promise;
}

async function persistSnapshotInner(docId: string): Promise<void> {
  let attempts = 0;
  // A single 401 may just be the JWT having ticked past expiry between the
  // last refresh and this PUT (clock skew, 5-second-skew TTL boundary, or
  // a forced session cutoff that we can recover from). Try a token refresh
  // before classifying as permanent — but only ONCE per snapshot run, so a
  // genuinely-revoked session can't loop. If the refresh succeeds, we
  // retry the PUT immediately without bumping `attempts`.
  let refreshAttempted = false;

  while (true) {
    const state = docs.get(docId);
    if (!state || !state.vaultKey) {
      // Doc unloaded or vault locked; bail. The pendingSync IndexedDB row
      // (written below on every iteration) is the durable signal; the boot
      // recovery / recurring flush will pick it up later.
      setRetryStatus(docId, null);
      return;
    }

    const yjsState = toB64(Y.encodeStateAsUpdate(state.ydoc));
    if (state.lastPushed === yjsState) {
      // Nothing has changed since the last successful PUT — short-circuit.
      setRetryStatus(docId, null);
      return;
    }

    const meta = state.meta;
    const plain: DocumentPlain = {
      title: (meta.get('title') as string | undefined) ?? '',
      markdown: state.derivedMarkdown,
      icon: meta.get('icon') as string | undefined,
      color: meta.get('color') as string | undefined,
      tags: meta.get('tags') as string[] | undefined,
      yjsState,
    };

    const payload: EncryptedPayload = await encryptJson(plain, state.vaultKey);

    // Mirror to IndexedDB on every iteration so the local cache is at most
    // one Y.Doc transaction stale. The flag stays pendingSync:true until
    // the PUT actually lands, so a tab-close mid-retry leaves a row the
    // boot recovery will pick up.
    const nowIso = new Date().toISOString();
    try {
      const row = await localStore.getDocument(docId);
      await localStore.putDocument({
        id: docId,
        createdAt: row?.createdAt ?? nowIso,
        updatedAt: nowIso,
        deletedAt: row?.deletedAt ?? null,
        pendingSync: true,
        data: {
          id: docId,
          customerId: state.customerId,
          parentId: state.parentId,
          payload,
          deletedAt: row?.deletedAt ?? null,
          createdAt: row?.createdAt ?? nowIso,
          updatedAt: nowIso,
        },
      });
      noteIdbWriteSuccess();
    } catch (err) {
      noteIdbWriteError(err);
    }

    snapshotInFlight.add(docId);
    try {
      // Only attach expectedVersion when we hold a real server-verified
      // version (>=1). On a freshly-created doc the local IndexedDB row's
      // `payload.version` is 0 (the EncryptedPayload's encryption-format
      // field) until pullAll overlays the server DTO, so acquireDoc can
      // legitimately seed lastKnownVersion=0. Sending expectedVersion=0
      // would fail the server's `z.number().int().min(1).optional()`
      // schema with `400 invalid`. Omit it instead — the lock degrades
      // to last-writer-wins for the first PUT, then the response's
      // `result.version` arms the lock for every subsequent PUT.
      const expectedVersion =
        typeof state.lastKnownVersion === 'number' && state.lastKnownVersion >= 1
          ? state.lastKnownVersion
          : undefined;
      const result = await api.updateDocument(docId, {
        customerId: state.customerId,
        parentId: state.parentId,
        payload,
        expectedVersion,
      });
      state.lastKnownVersion = result.version;
      state.lastPushed = yjsState;
      // Flip the local row to pendingSync:false now that the bytes are durable.
      try {
        const row = await localStore.getDocument(docId);
        if (row) {
          row.pendingSync = false;
          await localStore.putDocument(row);
        }
      } catch (err) {
        noteIdbWriteError(err);
      }
      setRetryStatus(docId, null);
      return;
    } catch (err: any) {
      snapshotInFlight.delete(docId);

      // 401-with-fresh-token-recovery: before treating 401 as permanent
      // (which would drop pendingSync and lose the edit), try a one-shot
      // JWT refresh. A successful refresh re-arms the next PUT with a fresh
      // token; the loop continues without incrementing `attempts`. If the
      // refresh itself fails, fall through to the standard permanent path.
      if (err?.status === 401 && !refreshAttempted) {
        refreshAttempted = true;
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          // Don't bump attempts — this isn't really a retry, it's a fresh
          // token rotation. Snapshot bytes are already encoded; loop again.
          continue;
        }
      }

      if (isPermanentError(err)) {
        // 404/401/403/410 — unrecoverable. Drop pendingSync so it doesn't
        // re-enter the boot recovery loop forever, and surface to the UI.
        try {
          const row = await localStore.getDocument(docId);
          if (row) {
            row.pendingSync = false;
            await localStore.putDocument(row);
          }
        } catch (err2) {
          noteIdbWriteError(err2);
        }
        setRetryStatus(docId, {
          attempts,
          retrying: false,
          lastError: err?.body?.error ?? err?.message ?? `${err?.status ?? 'permanent'}`,
        });
        emitPermanentError({
          documentId: docId,
          status: err?.status ?? 0,
          message: err?.body?.error ?? err?.message ?? 'Permanent snapshot failure',
        });
        return;
      }

      attempts++;
      setRetryStatus(docId, {
        attempts,
        retrying: true,
        lastError: err?.body?.error ?? err?.message ?? String(err?.status ?? err),
      });

      if (err?.status === 409) {
        // Concurrent writer beat us — CRDT-merge their state into our Y.Doc
        // (no edits lost on either side), bump the tracked version, loop.
        // Notify the UI once per conflict so the user sees a "merged with
        // another editor" toast instead of an unexplained text shift.
        await mergeFreshServerState(docId);
        emitConflictMerged({ documentId: docId });
        continue;
      }

      // Network / 5xx — exponential backoff and retry.
      await sleep(backoffMs(attempts));
    }
  }
}

/**
 * Pull the server's current payload for `docId`, decrypt it, merge its
 * yjsState into the local Y.Doc, and update the tracked row version so the
 * next PUT's optimistic lock passes.
 *
 * Forces past `pullAll`'s clobber-guard by clearing `pendingSync` on the
 * local row first — the retry loop's next iteration will re-mirror with
 * `pendingSync:true` once the merged state is encoded.
 */
async function mergeFreshServerState(docId: string): Promise<void> {
  const state = docs.get(docId);
  if (!state || !state.vaultKey) return;
  try {
    const row = await localStore.getDocument(docId);
    if (row?.pendingSync) {
      row.pendingSync = false;
      await localStore.putDocument(row);
    }
  } catch (err) {
    noteIdbWriteError(err);
  }
  await pullAll();
  const fresh = await localStore.getDocument(docId);
  if (!fresh) return;
  try {
    const plain = await decryptJson<DocumentPlain>(fresh.data.payload, state.vaultKey);
    if (plain.yjsState) {
      // Route through `applyServerSnapshot` (NOT a bare `Y.applyUpdate`) so
      // the `onRemoteApplied` listener chain fires — without that, a
      // read-only viewer's PM view doesn't repaint after the merge and the
      // DOM stays stale until they navigate away. The "broadcast merged
      // delta to peers on the old state" concern is already covered by the
      // next persistSnapshot loop iteration: we re-encode and PUT, the
      // server emits change_event, peers re-merge.
      applyServerSnapshot(docId, plain.yjsState);
    }
  } catch (err) {
    console.warn('[yjs] failed to merge fresh server state after 409', err);
  }
  state.lastKnownVersion = fresh.data.payload.version;
}

/**
 * Boot-time abandoned-write recovery. Scans IndexedDB for any document row
 * with `pendingSync:true` that isn't currently loaded in memory, decodes
 * its yjsState, briefly registers a Y.Doc, and runs the full retry loop.
 * Either the row syncs (admin and Dropbox finally see the content) or a
 * permanent-error is emitted to the UI for user resolution.
 *
 * Called from the post-unlock boot path. Re-callable; safe to invoke on
 * every reconnect / online event in addition to boot.
 */
export async function recoverPendingDocuments(vaultKey: CryptoKey): Promise<void> {
  const rows = await localStore.listDocuments();
  for (const row of rows) {
    if (!row.pendingSync) continue;
    if (row.deletedAt) continue; // soft-deletes ride the outbox path
    if (docs.has(row.id)) continue; // already loaded; its own retry loop handles it
    try {
      const plain = await decryptJson<DocumentPlain>(row.data.payload, vaultKey);
      const ydoc = new Y.Doc();
      if (plain.yjsState) {
        try {
          Y.applyUpdate(ydoc, fromB64(plain.yjsState));
        } catch {
          /* corrupted yjsState — fall through to plaintext seed */
        }
      }
      const meta = ydoc.getMap<unknown>('meta');
      if (meta.size === 0) {
        ydoc.transact(() => {
          if (plain.title !== undefined) meta.set('title', plain.title);
          if (plain.icon !== undefined) meta.set('icon', plain.icon);
          if (plain.color !== undefined) meta.set('color', plain.color);
          if (plain.tags !== undefined) meta.set('tags', plain.tags);
        });
      }
      // Construct a minimal DocState and register it so persistSnapshot can
      // run its loop. refCount=0 because no editor is mounted; the loop
      // exits gracefully if the user navigates away (the IndexedDB row stays
      // pendingSync until the loop succeeds or hits a permanent error).
      const state: DocState = {
        ydoc,
        meta,
        awareness: new Awareness(ydoc),
        refCount: 0,
        customerId: row.data.customerId,
        parentId: row.data.parentId,
        remoteOrigin: Symbol('recover-remote'),
        vaultKey,
        derivedMarkdown: plain.markdown ?? '',
        // Force a push even if encoding matches; the row is pendingSync,
        // so server hasn't seen this state yet.
        lastPushed: undefined,
        lastKnownVersion: row.data.payload.version,
      };
      docs.set(row.id, state);
      try {
        await persistSnapshot(row.id);
      } finally {
        // Clean up only if a real editor hasn't grabbed it during the loop.
        const current = docs.get(row.id);
        if (current === state && current.refCount === 0) {
          current.awareness.destroy();
          current.ydoc.destroy();
          docs.delete(row.id);
        }
      }
    } catch (err) {
      console.warn('[recover] failed to replay', row.id, err);
    }
  }
}

